import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, CheckCircle2 } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { MagneticButton } from "../components/MagneticButton";
import { postJSON } from "../lib/api";
import { SEO } from "../components/SEO";

export const ForgotPassword = () => {
    const [email, setEmail] = useState("");
    const [status, setStatus] = useState("idle");
    const [devUrl, setDevUrl] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        if (status === "loading") return;
        setStatus("loading");
        try {
            const r = await postJSON("/auth/password/reset-request", { email });
            // Surface the dev-only URL so the tester can use it without SMTP
            if (r && r._dev_only_reset_url) {
                console.log("[forgot-password] dev-only reset URL:", r._dev_only_reset_url);
                setDevUrl(r._dev_only_reset_url);
            }
            setStatus("success");
        } catch (e2) {
            // Still show success-ish to avoid enumeration (server already always 200)
            setStatus("success");
        }
    };

    return (
        <div className="pt-32 md:pt-40 pb-20 mx-auto max-w-md px-5" data-testid="forgot-password-page">
            <SEO noindex title="Reset password" canonicalPath="/forgot-password" />
            <Reveal>
                <div className="rounded-2xl border border-white/[0.06] bg-surface/70 backdrop-blur-md p-7">
                    <span className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-accent-cyan/10 border border-accent-cyan/20">
                        <Mail className="w-5 h-5 text-accent-cyan" />
                    </span>
                    <h1
                        className="mt-5 font-display font-semibold text-2xl tracking-tight"
                        data-testid="forgot-title"
                    >
                        Reset your password
                    </h1>

                    {status === "success" ? (
                        <div className="mt-5 space-y-3" data-testid="forgot-success">
                            <div className="flex items-start gap-2.5">
                                <CheckCircle2 className="w-4 h-4 text-accent-mint mt-0.5 shrink-0" />
                                <p className="text-[13.5px] text-text-primary leading-relaxed">
                                    Check your inbox. If an account exists with that email, a reset
                                    link is on the way.
                                </p>
                            </div>
                            {devUrl && (
                                <div
                                    className="text-[11px] font-mono text-text-muted bg-canvas/60 border border-white/[0.06] rounded-md p-3 break-all"
                                    data-testid="forgot-dev-url"
                                >
                                    <p className="text-accent-amber mb-1">DEV ONLY — no SMTP wired:</p>
                                    <a
                                        href={devUrl}
                                        className="text-accent-cyan hover:underline"
                                    >
                                        {devUrl}
                                    </a>
                                </div>
                            )}
                        </div>
                    ) : (
                        <form onSubmit={submit} className="mt-5 space-y-3" data-testid="forgot-form">
                            <input
                                type="email"
                                required
                                placeholder="Your email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="w-full bg-canvas/60 border border-white/[0.07] rounded-md px-3 py-2.5 text-[13.5px] text-text-primary placeholder:text-text-dim outline-none focus:border-accent-cyan/50 transition-colors duration-200"
                                data-testid="forgot-email"
                            />
                            <MagneticButton
                                type="submit"
                                disabled={status === "loading"}
                                className="w-full py-2.5 rounded-md bg-accent-cyan text-canvas text-[13.5px] font-semibold hover:shadow-glow-cyan disabled:opacity-40 transition-shadow duration-200"
                                data-testid="forgot-submit"
                                ariaLabel="Send reset link"
                            >
                                {status === "loading" ? "Sending…" : "Send reset link"}
                            </MagneticButton>
                        </form>
                    )}
                    <p className="mt-5 text-center text-[12.5px] text-text-muted">
                        <Link to="/login" className="hover:text-accent-cyan transition-colors">
                            ← Back to login
                        </Link>
                    </p>
                </div>
            </Reveal>
        </div>
    );
};

export default ForgotPassword;
